import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit'
import _ from 'lodash'
import client from '../../api/client'
import { postFilterLiveStreams, postOpenLiveGame,  postSelectOdd } from '../../api/live'
import { DEFAULT_COLLAPSED_SECTIONS, DEFAULT_SPORT_TYPE, DEFAULT_SUB_SPORT_TYPE, ENV } from '../../app/const'
import { RootState } from '../../app/store'
import { championshipsSampleData, searchItemsSampleData, selectedOddsSampleData, sportTypesSampleData } from './sampleData'
import {  FetchStatus, LiveChampionships, SearchItems, Sports, SubSports } from './types'

const initialState = {
  championships: [] as LiveChampionships[],
  sports: [] as Sports[],
  subSports: null as SubSports[] | null,
  collapsedChampionships: [] as number[],
  favorites: [] as number[],
  selectedOdds: [] as number[],
  searchItems: [] as SearchItems[],
  currentSportType: DEFAULT_SPORT_TYPE,
  currentSubSportType: DEFAULT_SUB_SPORT_TYPE as string | null,
  currentGames: true,
  filterLiveStreams: false,
  isSearching: false,
  isSearch: false,
  searchQuery: '',
  collapsedSections: DEFAULT_COLLAPSED_SECTIONS,
  oddsScroll: {} as { [key: string]: number },
  liveGameSection: 'current' as 'current' | 'today',
  championshipsFetchStatus: 'idle' as FetchStatus,
  sportTypesFetchStatus: 'idle' as FetchStatus,
  searchItemsFetchStatus: 'idle' as FetchStatus,
  selectedOddsFetchStatus: 'idle' as FetchStatus,
}

export const fetchLiveChampionships = createAsyncThunk<LiveChampionships[], void, { state:  RootState } >(
  'live/fetchChampionships',
  async (_, { getState } ) => {
    const { currentGames, currentSportType, currentSubSportType} = getState().liveGames

    if (ENV === 'development') {
      return (await JSON.parse(championshipsSampleData)) as LiveChampionships[]
    } else {
      //const response = await client.post('/Mobile/Pages/LiveGames.aspx/GetTestData', "{'multiplier':100, 'snatchcount' : 16}")
      const response = await client.post(
        '/Mobile/Pages/LiveGames.aspx/GetData',
        {
          sportTypeId: String(currentSportType),
          loadCurrentGames: currentGames, 
          subSportTypeId: currentSubSportType
        }
      )
      return await JSON.parse(response.data.d) as LiveChampionships[]
    }
  }
)

export const fetchLiveSportTypes = createAsyncThunk<Sports>(
  'live/fetchSportTypes',
  async () => {
    if (ENV === 'development') {
      return (await JSON.parse(sportTypesSampleData)) as Sports
    } else {
      const response = await client.post('/Mobile/Pages/LiveGames.aspx/GetSportTypes', {})
      return await JSON.parse(response.data.d) as Sports
    }
  }
);

export const postToggleFavoriteGame = createAsyncThunk(
  'live/postToggleFavoriteGame',
  async (gameId: number, { dispatch }) => {
    dispatch(setFavorite({ gameId }))
    const response = await client.post(
      '/Mobile/Pages/LiveGames.aspx/AddRemoveFavoriteGame',
      {gameId: String(gameId)}
    )
    return response
  }
)

export const fetchLiveSearchItems = createAsyncThunk(
  'live/fetchSearchItems',
  async (searchQuery: string) => {
    if (ENV === 'development') {

      return (await JSON.parse(searchItemsSampleData)) as SearchItems
    } else {
      const response = await client.post(
        '/Mobile/Pages/LiveBetting.aspx/SearchItems',
        {searchText: String(searchQuery)}
      )
      return response.data.d as SearchItems
    }
  }
)

export const fetchSelectedOdds = createAsyncThunk(
  'live/fetchSelectedOdds',
  async () => {
    if (ENV === 'development') {
      return (await JSON.parse(selectedOddsSampleData)) as Number[]
    } else {
      const response = await client.post(
        '/Mobile/Pages/LiveGames.aspx/GetSelectedSnatches',
        {}
      )
      return (await JSON.parse(response.data.d)) as Number[]
    }
  }
)

export const liveSlice = createSlice({
  name: 'live',
  initialState,
  reducers: {
    selectSportType(state, action: PayloadAction<{ sportTypeId: string }>) {
      const { sportTypeId } = action.payload
      state.currentSportType = sportTypeId
      state.subSports =
        state.sports.find((s) => s.Id === String(sportTypeId))?.SubSportTypes ??
        null
      if (state.subSports && !state.currentSubSportType)
        state.currentSubSportType = sportTypeId
    },
    selectDefaultSportType(state) {
      let currentSportType = DEFAULT_SPORT_TYPE
      try {
        currentSportType =
          String(
            state.sports
              .map((s) => Number(s.Id))
              .filter((p) => p > 0)
              .sort()[0]
          ) ?? DEFAULT_SPORT_TYPE
      } catch {}

      state.currentSportType = currentSportType
    },
    selectSubSportType(
      state,
      action: PayloadAction<{ subSportTypeId: string }>
    ) {
      const { subSportTypeId } = action.payload
      state.currentSubSportType = subSportTypeId
    },
    selectOdd(
      state,
      action: PayloadAction<{
        Id: number
        gameId: number
        championshipId: number
      }>
    ) {
      const { Id, gameId, championshipId } = action.payload
      const index = state.selectedOdds.findIndex((o) => o === Id)
      const gameOdds = state.championships
        .find((c) => c.championatId === championshipId)
        ?.Games.find((g) => g.Id === gameId)
        ?.Odds.map((o) => Number(o.Id))

      postSelectOdd(Id)

      if (gameOdds)
        state.selectedOdds = state.selectedOdds.filter(
          (i) => !gameOdds.includes(Number(i))
        )
      if (index === -1) state.selectedOdds.push(Id)
    },
    setFavorite(state, action: PayloadAction<{ gameId: number }>) {
      const { gameId } = action.payload
      const { favorites } = state
      const index = favorites.findIndex((f) => f === gameId)

      if (index === -1) {
        favorites.push(gameId)
      } else {
        favorites.splice(index, 1)
      }
    },
    toggleChampionship(state, action: PayloadAction<{ championatId: number }>) {
      const { championatId } = action.payload
      const { collapsedChampionships } = state
      const index = collapsedChampionships.findIndex((c) => c === championatId)

      if (index === -1) {
        collapsedChampionships.push(championatId)
      } else {
        collapsedChampionships.splice(index, 1)
      }
    },
    filterLiveStreams(state) {
      state.filterLiveStreams = !state.filterLiveStreams
      postFilterLiveStreams()
    },
    openLiveGame(
      state,
      action: PayloadAction<{ gameId: number; sportTypeId: number }>
    ) {
      const { gameId, sportTypeId } = action.payload
      postOpenLiveGame(gameId, sportTypeId)
    },
    setSearchQuery(state, action: PayloadAction<{ text: string }>) {
      const { text } = action.payload
      state.searchQuery = text
    },
    toggleIsSearch(state, action: PayloadAction<boolean>) {
      state.isSearch = action.payload
    },
    toggleIsSearching(state, action: PayloadAction<boolean>) {
      state.isSearching = action.payload
    },
    setOddsScroll(
      state,
      action: PayloadAction<{ gameId: number; scroll: number }>
    ) {
      const { gameId, scroll } = action.payload
      state.oddsScroll[gameId] = scroll
    },
    setDefaultLiveState(state) {
      state.currentSubSportType = null
      state.currentGames = true
      state.collapsedSections = DEFAULT_COLLAPSED_SECTIONS
      state.isSearch = false
      state.isSearching = false
      state.searchQuery = ''
    },
    toggleSections(
      state,
      action: PayloadAction<{
        section: keyof typeof initialState.collapsedSections
        value: boolean
      }>
    ) {
      const { section, value } = action.payload
      state.collapsedSections[section] = value

      if (state.collapsedSections.today && state.collapsedSections.current) {
        if (section === 'current') {
          state.collapsedSections.today = false
        } else {
          state.collapsedSections.current = false
        }
      }
      state.currentGames = section === 'today' && value ? false : true
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLiveChampionships.pending, (state) => {
        state.championshipsFetchStatus = 'loading'
      })
      .addCase(
        fetchLiveChampionships.fulfilled,
        (state, action: PayloadAction<LiveChampionships[]>) => {
          state.championshipsFetchStatus = 'succeeded'
          state.championships = action.payload
          let favorites: number[] = []
          try {
            favorites = _.flatMap(
              action.payload.map((c) =>
                c.Games.map((g) => (g.fav === 'active' ? g.Id : false)).filter(
                  Boolean
                )
              )
            ) as number[]
          } catch {}

          if (!_.isEqual(state.favorites, favorites))
            state.favorites = favorites
        }
      )
      .addCase(fetchLiveChampionships.rejected, (state) => {
        state.championshipsFetchStatus = 'failed'
      })
      .addCase(fetchLiveSportTypes.pending, (state) => {
        state.sportTypesFetchStatus = 'loading'
      })
      .addCase(
        fetchLiveSportTypes.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.sportTypesFetchStatus = 'succeeded'
          state.sports = action.payload
        }
      )
      .addCase(fetchLiveSportTypes.rejected, (state) => {
        state.sportTypesFetchStatus = 'failed'
      })
      .addCase(fetchLiveSearchItems.pending, (state) => {
        state.searchItemsFetchStatus = 'loading'
      })
      .addCase(
        fetchLiveSearchItems.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.searchItemsFetchStatus = 'succeeded'
          state.searchItems = _.uniqBy(action.payload, (s) => s.GameId)
        }
      )
      .addCase(fetchLiveSearchItems.rejected, (state) => {
        state.searchItemsFetchStatus = 'failed'
      })
      .addCase(fetchSelectedOdds.pending, (state) => {
        state.selectedOddsFetchStatus = 'loading'
      })
      .addCase(
        fetchSelectedOdds.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.selectedOddsFetchStatus = 'succeeded'
          state.selectedOdds = action.payload
        }
      )
      .addCase(fetchSelectedOdds.rejected, (state) => {
        state.selectedOddsFetchStatus = 'failed'
      })
    // .addCase(postToggleFavoriteGame.pending, (state) => {
    //   state.postToggleFavoriteGameStatus = 'loading'
    // })
    // .addCase(postToggleFavoriteGame.fulfilled, (state) => {
    //   state.postToggleFavoriteGameStatus = 'succeeded'
    // })
    // .addCase(postToggleFavoriteGame.rejected, (state) => {
    //   state.postToggleFavoriteGameStatus = 'failed'
    // })
  },
})


export const {
  setFavorite,
  toggleChampionship,
  selectOdd,
  filterLiveStreams,
  openLiveGame,
  selectSportType,
  setSearchQuery,
  toggleIsSearch,
  toggleIsSearching,
  selectSubSportType,
  toggleSections,
  setOddsScroll,
  selectDefaultSportType,
  setDefaultLiveState,
} = liveSlice.actions
export const selectLiveGames = (state: RootState) => state.liveGames.championships
export const selectSportTypes = (state: RootState) => state.liveGames.sports
export const selectCollapsedChampionships = (state: RootState) => state.liveGames.collapsedChampionships
export const selectCurrentGames = (state: RootState) => state.liveGames.currentGames
export const selectCurrentSportType = (state: RootState) => state.liveGames.currentSportType
export const selectCurrentSubSportType = (state: RootState) => state.liveGames.currentSubSportType

export const championshipIsCollapsed = (championshipId: number): any =>{
  createSelector(selectCollapsedChampionships, (c) => c.includes(championshipId))
}
export default liveSlice.reducer
